/**
 * Provides the `isExternalURL` utility function for determining if a given URL string points to an origin different from the current page's origin.
 * @module isExternalURL
 */
/**
 * Determines if a given URL string points to an origin different from the current page's origin.
 *
 * **Behavior:**
 * 1. **Environment check:** Verifies if running in a browser context with `window.location.origin` available. If not, logs a warning and returns `false` (assuming non-external).
 * 2. **Input validation:** Checks if the input `url` is a non-empty string. If not (null, undefined, empty string), logs a warning and returns `true` (treating invalid input as potentially external/unsafe).
 * 3. **URL parsing:**
 *  - Attempts to construct a `new URL()` object using the input `url`.
 *  - Crucially, it provides `window.location.origin` as the base URL to the constructor. This allows relative paths (like `/path`, `page.html`) to be correctly resolved against the current origin.
 *  - If `url` is already an absolute URL, the provided base is ignored by the constructor.
 *  - If the URL constructor fails (e.g., due to invalid URL format or unsupported scheme), it catches the error, logs it, and returns `true` (treating unparseable URLs as external).
 * 4. **Origin comparison:** Compares the `origin` property of the successfully parsed `URL` object (`testUrl.origin`) with the current page's origin (`window.location.origin`). The `origin` includes protocol, hostname, and port (if non-default).
 * 5. **Return value:** Returns `true` if the origins differ, `false` otherwise.
 *
 * **Summary table:** (Assuming `window.location.origin` is `https://automatorplugin.com`)
 *
 * | Input URL (`url`)                     | `isExternalUrl( url )` Result | Notes                                      |
 * | :------------------------------------ | :---------------------------- | :----------------------------------------- |
 * | `/path`                               | `false`                       | Relative path resolves to current origin   |
 * | `page.html`                           | `false`                       | Relative path resolves to current origin   |
 * | `https://automatorplugin.com/abc`     | `false`                       | Absolute URL, same origin                  |
 * | `http://automatorplugin.com/abc`      | `true`                        | Different protocol                         |
 * | `https://automatorplugin.com:8080/abc`| `true`                        | Different port                             |
 * | `https://example.com/abc`             | `true`                        | Different hostname                         |
 * | `mailto:test@example.com`             | `true`                        | Different scheme/origin                    |
 * | `blob:https://automatorplugin.com/guid`| `false`                      | Blob URL inherits origin                   |
 * | `data:text/plain,Hello`               | `true`                        | Data URL has opaque origin                 |
 * | `invalid-url:`                        | `true`                        | Parsing fails                              |
 * | `''`                                  | `true`                        | Invalid input                              |
 * | `null` / `undefined`                  | `true`                        | Invalid input                              |
 * | (No `window.location.origin`)         | `false`                       | Non-browser or unavailable origin          |
 *
 * @since 6.5 Initial implementation.
 * @param {string | null | undefined} url - The URL string to check. Can be relative or absolute. Handles null/undefined input.
 * @return {boolean} `true` if the URL is determined to be external (different origin) or if the input/URL is invalid. Returns `false` if the URL shares the same origin as the current page or if the origin cannot be determined (non-browser environment).
 *
 * @example Importing the isExternalUrl utility
 * ```typescript
 * import { isExternalUrl } from '@automator/shared/utils/is-external-url';
 * ```
 *
 * @example Checking internal relative paths
 * ```typescript
 * isExternalUrl( '/internal/page' ); // => false
 * isExternalUrl( 'another-page.html' ); // => false
 * isExternalUrl( '../sibling/resource' ); // => false
 * ```
 *
 * @example Checking internal absolute paths
 * ```typescript
 * // Assuming current origin is '[https://automatorplugin.com](https://automatorplugin.com)'
 * isExternalUrl( '[https://automatorplugin.com/some/path?query=1#hash](https://automatorplugin.com/some/path?query=1#hash)' ); // => false
 * isExternalUrl( '[https://automatorplugin.com](https://automatorplugin.com)' ); // => false
 * ```
 *
 * @example Checking external URLs
 * ```typescript
 * // Assuming current origin is '[https://automatorplugin.com](https://automatorplugin.com)'
 * isExternalUrl( 'https://example.com' ); // => true (different hostname)
 * isExternalUrl( 'http://automatorplugin.com' ); // => true (different protocol: http vs https)
 * isExternalUrl( 'https://automatorplugin.com:8080' ); // => true (different port)
 * ```
 *
 * @example Checking different schemes
 * ```typescript
 * isExternalUrl( 'mailto:test@example.com' ); // => true
 * isExternalUrl( 'tel:123456' ); // => true
 * isExternalUrl( 'data:text/plain,test' ); // => true
 * ```
 *
 * @example Handling invalid inputs
 * ```typescript
 * isExternalUrl( '' ); // => true (logs warning)
 * isExternalUrl( null ); // => true (logs warning)
 * isExternalUrl( '::not a url::' ); // => true (logs error)
 * ```
 *
 * @remarks
 * - This function relies on the browser's `URL` constructor and `window.location.origin`. It may not work as expected or return `false` in non-browser environments (like Node.js) unless `window.location` is polyfilled.
 * - URLs that fail to parse are treated as external for safety.
 * - Empty strings, null, and undefined inputs are treated as invalid and thus external.
 * - Blob URLs inherit the origin of the document that created them and are typically considered internal if created by the current page.
 * - Data URLs have an opaque origin (`null`) and are always considered external by this comparison.
 * - For comprehensive behavior details, refer to the unit tests.
 */
export function isExternalUrl(url) {
    // 1. Check for browser environment and necessary properties
    if (typeof window === 'undefined' || !window.location || typeof window.location.origin !== 'string') {
        // Cannot determine origin if not in a browser context with window.location.origin
        console.warn('isExternalUrl: window.location.origin is not available. Assuming URL is not external.');
        return false;
    }
    const currentOrigin = window.location.origin;
    // 2. Handle obviously invalid or empty input string before URL constructor
    // Although URL constructor might handle empty string, explicit check is clearer.
    if (!url || typeof url !== 'string') {
        console.warn(`isExternalUrl: Invalid or empty URL input provided: "${url}". Treating as external.`);
        return true; // Treat fundamentally invalid input as external/unsafe
    }
    try {
        // 3. Resolve the URL relative to the current origin.
        // - If 'url' is relative (e.g., '/path', 'page.html'), it resolves using currentOrigin as base.
        // - If 'url' is absolute (e.g., 'https://other.com'), currentOrigin base is ignored.
        // - This constructor correctly handles various URL schemes.
        const testUrl = new URL(url, currentOrigin);
        // 4. Compare the origin of the resolved URL object with the current page's origin.
        // The 'origin' property includes protocol, hostname, and port (if specified).
        return testUrl.origin !== currentOrigin;
    }
    catch (error) {
        // 5. Handle errors during URL parsing (invalid URL format).
        console.error(`isExternalUrl: Failed to parse URL "${url}". Treating as external.`, error);
        // Treat URLs that cause parsing errors as external (or at least, not verifiably internal).
        return true;
    }
}
;
